import { Injectable } from '@angular/core';
import { ApiConstants } from '../constants/api-constants';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { ApiResponse } from 'src/models/api-response.model';
import { Router } from '@angular/router';
import { AppConstants } from '../constants/app-constants';
import { AppLocalStorage } from '../utils/local-storage';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  URL_COMM = ApiConstants.URL_AUTH;
  private user: any;
  private readonly userChanged = new Subject<void>();
  readonly userChanges$ = this.userChanged.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    const storedUser = localStorage.getItem(AppConstants.USER);
    this.user = storedUser ? JSON.parse(storedUser) : null;
  }

  async login(username: string, password: string): Promise<ApiResponse> {
    const URL = `${this.URL_COMM}/login`;
    password = btoa(password);
    const response = await firstValueFrom(this.http.post<any>(URL, { username, password }));
    if (response.success && response.data && 'authToken' in response.data) {
      localStorage.setItem(AppConstants.SESSION_TOKEN, response.data.authToken as string);
      localStorage.setItem(AppConstants.ROLE, response.data.role as string);
      this.setUser(response.data);
      // Prefer server profile (includes isAdmin) over the slim login payload
      try {
        await this.getProfile();
      } catch {
        // Keep login payload if /me fails; route guards still use role fallbacks
      }
    }

    return response;
  }

  async getProfile(): Promise<any> {
    const response = await firstValueFrom(this.http.get<any>(`${this.URL_COMM}/me`));
    if (response.success && response.data) {
      this.setUser(response.data);
      const roleName = response.data.role?.name || response.data.role;
      if (roleName) {
        localStorage.setItem(AppConstants.ROLE, roleName);
      }
    }
    return response.data;
  }

  private roleName(user: any = this.getUser()): string {
    return String(user?.role?.name || user?.role || '').trim().toLowerCase();
  }

  isAdmin(): boolean {
    const user = this.getUser();
    if (!user) {
      return false;
    }
    if (user.isAdmin === true) {
      return true;
    }
    return AppConstants.ADMIN_ROLES.includes(this.roleName(user));
  }

  isTeacher(): boolean {
    const user = this.getUser();
    if (!user) {
      return false;
    }
    if (user.isTeacher === true) {
      return true;
    }
    return this.roleName(user) === AppConstants.TEACHER_ROLE;
  }

  isSupervisor(): boolean {
    const user = this.getUser();
    if (!user) {
      return false;
    }
    if (user.isSupervisor === true) {
      return true;
    }
    return this.roleName(user) === AppConstants.SUPERVISOR_ROLE;
  }

  /** Supervisors can view plans but cannot change them. */
  isReadOnly(): boolean {
    return this.isSupervisor();
  }

  setUser(user: any) {
    this.user = user;
    localStorage.setItem(AppConstants.USER, JSON.stringify(user));
    this.userChanged.next();
  }

  getUser() {
    if (!this.user) {
      const storedUser = localStorage.getItem(AppConstants.USER);
      this.user = storedUser ? JSON.parse(storedUser) : null;
    }
    return this.user;
  }

  async logout(): Promise<ApiResponse> {
    const URL = `${this.URL_COMM}/logout`;
    const response = await firstValueFrom(this.http.post<ApiResponse>(URL, {}));
    if (response.success) {
      this.router.navigate(['/']);
      this.user = null;
      AppLocalStorage.logout();
    }
    return response;
  }

  async isValidSession(): Promise<boolean> {
    const URL = `${this.URL_COMM}/isValid`;
    try {
      const response = await firstValueFrom(this.http.get<any>(URL));
      const valid = response?.data === true;
      if (!valid) {
        this.router.navigate(['/']);
        AppLocalStorage.logout();
      }
      return valid;
    } catch {
      return false;
    }
  }
}
